/**
 * Organization Management Types
 */

import type { MemberBillingInfo } from '@store/generated/organizations';

export interface MemberBundle {
  assignment_id: string;
  bundle_id: string;
  bundle_name: string;
  bundle_type: 'tier' | 'add_on' | 'data_security';
  status: string;
}

export interface OrganizationMemberTableRow {
  userId: string;
  name: string;
  email: string;
  roles: string[];
  status: 'active' | 'inactive';
  deactivatedAt?: string | null;
  notesCreated: number;
  phoneNumber?: string | null;
  profilePicture?: string | null;
  bundles: MemberBundle[];
  billingInfo?: MemberBillingInfo | null;
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Lightweight clinician representation for dropdowns and filters.
 * Derived from ProviderOrganizationMemberResponse with role='clinician'.
 */
export interface Clinician {
  userId: string;
  name: string;
}

export interface OrganizationInvitation {
  invitationId: string;
  email: string;
  role: string;
  sentAt: string;
  expiresAt: string;
  status?: string;
  bundleIds?: string[];
}

export interface OrganizationInfo {
  name: string;
  memberCount: number;
  inactiveMemberCount: number;
  seatCount: number | null;
  subscriptionStatus: string | null;
  billingPortalUrl: string | null;
  createdAt: string | null;
}

export interface ConfirmDialogState {
  open: boolean;
  userId?: string;
  role?: string;
  action?: 'remove' | 'add' | 'reactivate';
}

export interface LoadingOperations {
  [key: string]: boolean;
}

// ============================================================================
// Permissions Matrix Config Types (Statsig Dynamic Config)
// ============================================================================

/**
 * Permission status for a role-permission combination
 */
export type PermissionStatus = 'default' | 'configurable' | 'coming_soon' | 'none';

/**
 * Role definition in the permissions matrix
 */
export interface PermissionsMatrixRole {
  id: string;
  label: string;
}

/**
 * Permission definition with statuses for each role
 */
export interface PermissionsMatrixPermission {
  id: string;
  label: string;
  statuses: Record<string, PermissionStatus>;
}

/**
 * Legend labels for the permission statuses
 */
export interface PermissionsMatrixLegend {
  default: string;
  configurable: string;
  coming_soon: string;
}

/**
 * Complete configuration for the Permissions Matrix component
 * Controlled via Statsig Dynamic Config: permissions_matrix_config
 */
export interface PermissionsMatrixConfig {
  title: string;
  subtitle: string;
  notes?: string[];
  legend: PermissionsMatrixLegend;
  roles: PermissionsMatrixRole[];
  permissions: PermissionsMatrixPermission[];
}

/**
 * Default configuration for the Permissions Matrix
 * Used as fallback when Statsig config is not available
 */
export const DEFAULT_PERMISSIONS_MATRIX_CONFIG: PermissionsMatrixConfig = {
  title: 'Permissions',
  subtitle: 'Breakdown of permissions by role type for key actions in JotPsych',
  notes: ['Users can have multiple roles for flexible permissions'],
  legend: {
    default: 'default',
    configurable: 'Only available if a Practice Administrator changes the default setting',
    coming_soon: 'coming soon',
  },
  roles: [
    { id: 'practice_administrator', label: 'Practice Administrator' },
    { id: 'supervisor', label: 'Supervisor' },
    { id: 'clinician', label: 'Clinician' },
    { id: 'operations_support', label: 'Operations Support' },
    { id: 'billing_administrator', label: 'Billing Administrator' },
  ],
  permissions: [
    {
      id: 'create_edit_notes',
      label: 'Create and edit clinical notes',
      statuses: {
        practice_administrator: 'default',
        supervisor: 'default',
        clinician: 'default',
        operations_support: 'none',
        billing_administrator: 'none',
      },
    },
    {
      id: 'view_all_notes',
      label: 'View all clinical notes',
      statuses: {
        practice_administrator: 'default',
        supervisor: 'default',
        clinician: 'configurable',
        operations_support: 'default',
        billing_administrator: 'configurable',
      },
    },
    {
      id: 'access_patient_records',
      label: 'Access all patient records',
      statuses: {
        practice_administrator: 'default',
        supervisor: 'configurable',
        clinician: 'configurable',
        operations_support: 'none',
        billing_administrator: 'none',
      },
    },
    {
      id: 'submit_claims',
      label: 'Submit claims for billing',
      statuses: {
        practice_administrator: 'coming_soon',
        supervisor: 'coming_soon',
        clinician: 'coming_soon',
        operations_support: 'coming_soon',
        billing_administrator: 'coming_soon',
      },
    },
    {
      id: 'manage_members',
      label: 'Manage organizational members',
      statuses: {
        practice_administrator: 'default',
        supervisor: 'none',
        clinician: 'none',
        operations_support: 'none',
        billing_administrator: 'none',
      },
    },
    {
      id: 'manage_subscription',
      label: 'Manage subscription',
      statuses: {
        practice_administrator: 'default',
        supervisor: 'none',
        clinician: 'none',
        operations_support: 'none',
        billing_administrator: 'default',
      },
    },
    {
      id: 'sign_supervised_notes',
      label: 'Sign supervised notes',
      statuses: {
        practice_administrator: 'none',
        supervisor: 'coming_soon',
        clinician: 'none',
        operations_support: 'none',
        billing_administrator: 'none',
      },
    },
    {
      id: 'manage_templates',
      label: 'Manage organizational templates',
      statuses: {
        practice_administrator: 'default',
        supervisor: 'configurable',
        clinician: 'configurable',
        operations_support: 'none',
        billing_administrator: 'none',
      },
    },
  ],
};